import { useState } from 'react';
import { Link } from 'react-router-dom';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d95qomqh9roq6qfrc93g';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('website_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setEmail('');
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setEmail('');
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText;
        setStatus('error');
        setErrorMsg(String(msg));
      }
    } catch {
      setStatus('error');
      setErrorMsg('Network error. Please try again.');
    }
  };

  return (
    <footer className="bg-background-950 text-background-50">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 1px, transparent 1px), radial-gradient(circle at 70% 60%, rgba(255,255,255,0.2) 1px, transparent 1px)',
            backgroundSize: '60px 60px, 80px 80px',
          }}
        ></div>

        <div className="relative max-w-[1280px] mx-auto px-4 md:px-6 py-16 md:py-20">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            <div className="w-full lg:w-[35%]">
              <Link to="/" className="inline-flex items-center gap-1.5 font-heading font-semibold text-lg text-background-50">
                cssspreadsheet.rest
                <span className="w-[4px] h-[4px] rounded-full bg-primary-400 inline-block ml-0.5"></span>
              </Link>
              <p className="mt-4 text-sm text-background-400 leading-relaxed max-w-[300px]">
                The largest free CSSBuy spreadsheet directory. Discover verified products, trusted sellers, and the latest fashion finds.
              </p>

              <form
                data-readdy-form
                id="newsletter-footer"
                onSubmit={handleNewsletterSubmit}
                className="mt-6 flex items-center gap-2"
              >
                <div className="anti-spam-field">
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>
                <div className="relative flex-1">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email for updates"
                    required
                    className="w-full px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-400 focus:outline-none focus:border-background-50/40 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-500 text-background-50 hover:bg-primary-400 transition-colors cursor-pointer flex-shrink-0"
                  aria-label="Subscribe"
                >
                  <i className="ri-notification-3-line text-lg"></i>
                </button>
              </form>

              {status === 'success' && (
                <p className="mt-2 text-xs text-accent-400">Subscribed! Thanks for joining.</p>
              )}
              {status === 'error' && (
                <p className="mt-2 text-xs text-red-400">{errorMsg || 'Something went wrong.'}</p>
              )}

              <p className="mt-6 font-heading text-xl md:text-2xl text-background-50 leading-snug">
                Your gateway to
                <br />
                verified CSSBuy finds
              </p>
            </div>

            <div className="w-full lg:w-[65%] grid grid-cols-2 md:grid-cols-4 gap-8">
              <div>
                <h4 className="font-heading font-semibold text-sm text-background-50 mb-4">
                  Categories
                </h4>
                <div className="flex flex-col gap-2.5">
                  <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Shoes</a>
                  <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Hoodies/Sweaters</a>
                  <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">T-Shirts</a>
                  <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Jackets</a>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm text-background-50 mb-4">
                  More Categories
                </h4>
                <div className="flex flex-col gap-2.5">
                  <a href="https://kaspreadsheet.com/search-915/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Pants/Shorts</a>
                  <a href="https://kaspreadsheet.com/search-916/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Headwear</a>
                  <a href="https://kaspreadsheet.com/search-920/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Accessories</a>
                  <a href="https://kaspreadsheet.com/search-921/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Bags</a>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm text-background-50 mb-4">
                  Resources
                </h4>
                <div className="flex flex-col gap-2.5">
                  <a href="/article/cssbuy-sheet-guide" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">CSSBuy Sheet Guide</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">kaspreadsheet.com</a>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Buy It Now</a>
                  <a href="#categories" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">All Categories</a>
                </div>
              </div>
              <div>
                <h4 className="font-heading font-semibold text-sm text-background-50 mb-4">
                  Quick Links
                </h4>
                <div className="flex flex-col gap-2.5">
                  <a href="https://kaspreadsheet.com/search-923/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Perfumes</a>
                  <a href="https://kaspreadsheet.com/search-917/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Sets</a>
                  <a href="https://kaspreadsheet.com/search-919/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Jersey</a>
                  <a href="https://kaspreadsheet.com/search-924/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-background-300 hover:text-background-50 transition-colors whitespace-nowrap">Other Stuff</a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-14 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-background-400">
              &copy; 2026 cssspreadsheet.rest. All rights reserved.
            </p>
            <div className="flex items-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 rounded-lg border border-background-50/20 flex items-center justify-center text-background-300 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-global-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 rounded-lg border border-background-50/20 flex items-center justify-center text-background-300 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 rounded-lg border border-background-50/20 flex items-center justify-center text-background-300 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-sm"></i>
              </a>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 rounded-lg border border-background-50/20 flex items-center justify-center text-background-300 hover:text-background-50 hover:border-background-50/40 transition-colors cursor-pointer">
                <i className="ri-discord-line text-sm"></i>
              </a>
            </div>
            <a href="/" className="text-xs text-background-400 hover:text-background-300 transition-colors whitespace-nowrap">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}